import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../LanguageContext'; // Importamos el contexto de idioma

const NotFound = () => {
  const { t } = useLanguage(); // Hook de idioma
  const [count, setCount] = useState(10);
  const [animationActive, setAnimationActive] = useState(false);
  
  // Efecto para la cuenta regresiva de redirección
  useEffect(() => {
    const timer = count > 0 && setInterval(() => setCount(count - 1), 1000);
    if (count === 0) {
      window.location.href = '/';
    }
    return () => clearInterval(timer);
  }, [count]);
  
  // Efecto para activar animaciones después de cargar
  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimationActive(true);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);
  
  return (
    <div className="bg-black text-white min-h-screen flex flex-col justify-center items-center relative overflow-hidden">
      {/* Efectos de fondo */}
      <div className="absolute inset-0 z-0">
        <div className="absolute left-0 top-1/4 w-full h-px bg-red-600/30 animate-growWidth"></div>
        <div className="absolute left-0 top-2/4 w-full h-px bg-red-600/20 animate-growWidthDelayed"></div>
        <div className="absolute left-0 top-3/4 w-full h-px bg-red-600/10 animate-growWidthDelayed2"></div>
        
        {/* Efecto de círculo radial */}
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full"
          style={{ background: 'radial-gradient(circle at center, rgba(220, 38, 38, 0.1) 0%, rgba(0, 0, 0, 0) 70%)' }}></div>
      </div>
      
      {/* Contenido principal */}
      <div className="container mx-auto px-6 relative z-10 text-center">
        {/* Número 404 grande y animado */}
        <div className={`text-[180px] md:text-[220px] font-black leading-none transition-all duration-1000 ${animationActive ? 'opacity-100' : 'opacity-0'}`}
             style={{ 
               backgroundImage: 'linear-gradient(135deg, #dc2626 0%, #ef4444 25%, #ffffff 50%, #ef4444 75%, #dc2626 100%)',
               backgroundSize: '200% auto',
               color: 'transparent',
               backgroundClip: 'text',
               WebkitBackgroundClip: 'text',
               animation: 'gradientFlow 4s linear infinite'
             }}>
          404
        </div>
        
        {/* Mensaje de error */}
        <h1 className={`text-3xl md:text-4xl font-bold mb-6 transition-all duration-1000 delay-300 ${animationActive ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform translate-y-4'}`}>
          {t('error404.title') || 'Página no encontrada'}
        </h1>
        
        <div className="w-24 h-1 bg-gradient-to-r from-red-600 to-red-400 mx-auto mb-8 rounded-full shadow-lg shadow-red-600/20 transition-all duration-1000 delay-400"
             style={{ width: animationActive ? '96px' : '0' }}></div>
        
        <p className={`text-lg text-gray-300 max-w-2xl mx-auto mb-12 transition-all duration-1000 delay-500 ${animationActive ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform translate-y-4'}`}>
          {t('error404.message') || 'Lo sentimos, la página que estás buscando no existe o ha sido movida. Serás redirigido a la página de inicio en'} <span className="text-red-500 font-bold">{count}</span> {t('error404.seconds') || 'segundos'}.
        </p>
        
        {/* Botones de acción */}
        <div className={`flex flex-wrap justify-center gap-6 transition-all duration-1000 delay-700 ${animationActive ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform translate-y-6'}`}>
          <Link to="/" className="group relative inline-block">
            <div className="absolute -inset-1 bg-gradient-to-r from-red-600 to-red-400 rounded-full blur-md opacity-70 group-hover:opacity-100 transition-opacity duration-300 animate-pulse"></div>
            <div className="relative px-8 py-4 bg-gradient-to-r from-red-700 to-red-500 rounded-full text-white font-bold overflow-hidden shadow-xl">
              {/* Button background animation */}
              <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-red-600 to-red-400 transition-transform duration-500 transform translate-x-full group-hover:translate-x-0"></div>
              
              {/* Button content */}
              <div className="relative z-10 flex items-center justify-center space-x-2">
                <span className="uppercase tracking-wider text-md">VOLVER AL INICIO</span>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </div>
            </div>
          </Link>
          
          <Link to="/atletas" className="px-8 py-4 border border-white hover:border-red-600 hover:text-red-600 text-white rounded-full font-bold transition-all duration-300 uppercase tracking-wider">
            VER ATLETAS
          </Link>
        </div>
        
        {/* Logo AFY Group */}
        <div className={`mt-16 transition-all duration-1000 delay-800 ${animationActive ? 'opacity-100' : 'opacity-0'}`}>
          <div className="text-center">
            <div className="text-red-600 text-4xl font-bold mb-2">AFY</div>
            <div className="text-xl font-medium text-white">GROUP</div>
            <div className="mt-2 text-xs text-gray-400">FOOTBALL MANAGEMENT</div>
          </div>
        </div>
      </div>
      
      {/* Jugador desenfocado en el fondo (opcional) */}
      <div className="absolute bottom-0 right-0 opacity-20 h-2/3 pointer-events-none">
        <div className="relative h-full w-64 md:w-80">
          <div className="absolute bottom-0 right-0 w-full h-full bg-gradient-to-t from-black to-transparent z-10"></div>
        </div>
      </div>
      
      {/* CSS adicional para animaciones */}
      <style jsx>{`
        @keyframes growWidth {
          from { width: 0; }
          to { width: 100%; }
        }
        
        @keyframes growWidthDelayed {
          0% { width: 0; }
          30% { width: 0; }
          100% { width: 100%; }
        }
        
        @keyframes growWidthDelayed2 {
          0% { width: 0; }
          60% { width: 0; }
          100% { width: 100%; }
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes gradientFlow {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        
        .animate-growWidth {
          animation: growWidth 3s forwards;
        }
        
        .animate-growWidthDelayed {
          animation: growWidthDelayed 4s forwards;
        }
        
        .animate-growWidthDelayed2 {
          animation: growWidthDelayed2 5s forwards;
        }
        
        .animate-fadeInUp {
          animation: fadeInUp 1s forwards;
        }
      `}</style>
    </div>
  );
};

export default NotFound;